import { useEffect, useMemo, useState, Fragment } from 'react';
import { apiService } from '../services/api.service';

const ACTIONS = [
  { code: 'read', label: 'Ver' },
  { code: 'write', label: 'Editar' },
  { code: 'critical', label: 'Crítico' },
] as const;

const FALLBACK_MODULES = [
  { code: 'dashboard', label: 'Tablero', group: 'dashboard', groupLabel: 'Tablero', criticalPoints: [] as string[] },
  { code: 'customers', label: 'Listado clientes', group: 'operations', groupLabel: 'Operativo', criticalPoints: [] as string[] },
  { code: 'applications', label: 'Presolicitudes pendientes', group: 'operations', groupLabel: 'Operativo', criticalPoints: [] as string[] },
  { code: 'loans', label: 'Créditos', group: 'operations', groupLabel: 'Operativo', criticalPoints: [] as string[] },
  { code: 'payments', label: 'Pagos', group: 'collections', groupLabel: 'Cobranzas', criticalPoints: [] as string[] },
  { code: 'collections-desk', label: 'Cobranzas (estación)', group: 'collections', groupLabel: 'Cobranzas', criticalPoints: [] as string[] },
  { code: 'cash-mine', label: 'Mi caja', group: 'treasury', groupLabel: 'Tesorería', criticalPoints: [] as string[] },
];

type CatalogModule = {
  code: string;
  label: string;
  group?: string;
  groupLabel?: string;
  criticalPoints?: string[];
};

const STAFF_ROLES = [
  { value: 'ADMIN', label: 'Administrador' },
  { value: 'MANAGER', label: 'Manager / Supervisor' },
  { value: 'LOAN_OFFICER', label: 'Oficial de créditos' },
  { value: 'COLLECTION_OFFICER', label: 'Oficial de cobranzas' },
  { value: 'ACCOUNTANT', label: 'Contador' },
  { value: 'AUDITOR', label: 'Auditor' },
] as const;

const emptyCreateForm = () => ({
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  phone: '',
  role: 'LOAN_OFFICER',
  legacyTemplate: '',
  branchIds: [] as string[],
});

export function Users() {
  const [users, setUsers] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [registers, setRegisters] = useState<any[]>([]);
  const [catalog, setCatalog] = useState<any | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'idle' | 'create' | 'edit'>('idle');
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [tab, setTab] = useState<'profile' | 'branches' | 'cash' | 'modules'>('profile');
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    role: 'LOAN_OFFICER',
    isActive: true,
  });
  const [grantMatrix, setGrantMatrix] = useState<Record<string, Record<string, boolean>>>({});
  const [cashAccessMap, setCashAccessMap] = useState<Record<string, {
    canOpen: boolean;
    canClose: boolean;
    canMove: boolean;
    canTransfer: boolean;
    canAudit: boolean;
    isDefault: boolean;
  }>>({});

  const modules: CatalogModule[] = useMemo(() => {
    const raw = catalog?.modules;
    if (!Array.isArray(raw) || raw.length === 0) return FALLBACK_MODULES;
    return raw.map((m: string | CatalogModule) =>
      typeof m === 'string'
        ? { code: m, label: m, criticalPoints: [] }
        : {
            code: m.code,
            label: m.label || m.code,
            group: m.group,
            groupLabel: m.groupLabel,
            criticalPoints: m.criticalPoints ?? [],
          },
    );
  }, [catalog]);

  const moduleGroups = useMemo(() => {
    const groups: { key: string; label: string; items: CatalogModule[] }[] = [];
    const index = new Map<string, number>();
    for (const m of modules) {
      const key = m.group || m.code;
      const label = m.groupLabel || m.label;
      let i = index.get(key);
      if (i === undefined) {
        i = groups.length;
        index.set(key, i);
        groups.push({ key, label, items: [] });
      }
      groups[i].items.push(m);
    }
    return groups;
  }, [modules]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, b, r, c] = await Promise.all([
        apiService.listStaffUsers(),
        apiService.getBranches(true),
        apiService.getCashRegisters(),
        apiService.getPermissionsCatalog(),
      ]);
      setUsers(u);
      setBranches(b);
      setRegisters(r);
      setCatalog(c);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  const openUser = async (id: string) => {
    setMode('edit');
    setSelectedId(id);
    setBusy(true);
    setError(null);
    try {
      const d = await apiService.getUserPermissions(id);
      setDetail(d);
      setProfileForm({
        firstName: d.user?.firstName ?? '',
        lastName: d.user?.lastName ?? '',
        email: d.user?.email ?? '',
        phone: d.user?.phone ?? '',
        password: '',
        role: d.user?.role ?? 'LOAN_OFFICER',
        isActive: d.user?.isActive !== false,
      });
      setTab('profile');
      setSelectedBranches(d.branches?.map((x: any) => x.branchId) ?? []);
      const matrix: Record<string, Record<string, boolean>> = {};
      for (const m of modules) {
        matrix[m.code] = { read: false, write: false, critical: false };
      }
      for (const g of d.effectiveGrants ?? d.grants ?? []) {
        if (!matrix[g.module]) matrix[g.module] = { read: false, write: false, critical: false };
        matrix[g.module][g.action] = g.granted;
      }
      setGrantMatrix(matrix);
      const cashMap: typeof cashAccessMap = {};
      for (const c of d.cashAccess ?? []) {
        cashMap[c.cashRegisterId] = {
          canOpen: c.canOpen ?? false,
          canClose: c.canClose ?? false,
          canMove: c.canMove ?? true,
          canTransfer: c.canTransfer ?? false,
          canAudit: c.canAudit ?? false,
          isDefault: c.isDefault ?? false,
        };
      }
      setCashAccessMap(cashMap);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
      setSelectedId(null);
      setMode('idle');
    } finally {
      setBusy(false);
    }
  };

  const startCreate = () => {
    setMode('create');
    setSelectedId(null);
    setDetail(null);
    setCreateForm(emptyCreateForm());
    setError(null);
  };

  const createUser = async () => {
    const email = createForm.email.trim();
    const firstName = createForm.firstName.trim();
    const lastName = createForm.lastName.trim();
    if (!email || !firstName || !lastName || createForm.password.length < 6) {
      setError('Completá email, nombre, apellido y una contraseña de al menos 6 caracteres.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await apiService.createStaffUser({
        email,
        password: createForm.password,
        firstName,
        lastName,
        phone: createForm.phone.trim() || undefined,
        role: createForm.role,
      });
      if (createForm.legacyTemplate) {
        await apiService.applyLegacyRole(created.id, createForm.legacyTemplate);
      }
      if (createForm.branchIds.length > 0) {
        await apiService.setUserBranches(
          created.id,
          createForm.branchIds.map((branchId) => ({ branchId })),
        );
      }
      const refreshed = await apiService.listStaffUsers();
      setUsers(refreshed);
      await openUser(created.id);
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : (msg ?? e.message));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Re-open matrix keys when catalog modules load after first open
  useEffect(() => {
    if (!selectedId || modules.length === 0) return;
    setGrantMatrix((prev) => {
      const next = { ...prev };
      for (const m of modules) {
        if (!next[m.code]) next[m.code] = { read: false, write: false, critical: false };
      }
      return next;
    });
  }, [modules, selectedId]);

  const applyLegacy = async (legacyRole: string) => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await apiService.applyLegacyRole(selectedId, legacyRole);
      await openUser(selectedId);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = async () => {
    if (!selectedId) return;
    const email = profileForm.email.trim();
    const firstName = profileForm.firstName.trim();
    const lastName = profileForm.lastName.trim();
    if (!email || !firstName || !lastName) {
      setError('Nombre, apellido y email son obligatorios');
      return;
    }
    if (profileForm.password && profileForm.password.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        email,
        firstName,
        lastName,
        phone: profileForm.phone.trim() || null,
        role: profileForm.role,
        isActive: profileForm.isActive,
      };
      if (profileForm.password.trim()) payload.password = profileForm.password.trim();
      await apiService.updateStaffUser(selectedId, payload);
      setProfileForm((f) => ({ ...f, password: '' }));
      await openUser(selectedId);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveBranches = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await apiService.setUserBranches(selectedId, selectedBranches.map((branchId) => ({ branchId })));
      await openUser(selectedId);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveCashAccess = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const cashAccess = Object.entries(cashAccessMap).map(([cashRegisterId, flags]) => ({
        cashRegisterId,
        ...flags,
      }));
      await apiService.setUserCashAccess(selectedId, cashAccess);
      await openUser(selectedId);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleCashRegister = (registerId: string) => {
    setCashAccessMap((prev) => {
      if (prev[registerId]) {
        const next = { ...prev };
        delete next[registerId];
        const remaining = Object.keys(next);
        if (remaining.length > 0 && !Object.values(next).some((x) => x.isDefault)) {
          next[remaining[0]] = { ...next[remaining[0]], isDefault: true };
        }
        return next;
      }
      const hasAny = Object.keys(prev).length > 0;
      return {
        ...prev,
        [registerId]: {
          canOpen: true,
          canClose: true,
          canMove: true,
          canTransfer: false,
          canAudit: false,
          isDefault: !hasAny,
        },
      };
    });
  };

  const setDefaultCashRegister = (registerId: string) => {
    setCashAccessMap((prev) => {
      if (!prev[registerId]) return prev;
      const next: typeof prev = {};
      for (const [id, flags] of Object.entries(prev)) {
        next[id] = { ...flags, isDefault: id === registerId };
      }
      return next;
    });
  };

  const toggleCashFlag = (
    registerId: string,
    flag: 'canOpen' | 'canClose' | 'canMove' | 'canTransfer' | 'canAudit',
  ) => {
    setCashAccessMap((prev) => ({
      ...prev,
      [registerId]: { ...prev[registerId], [flag]: !prev[registerId]?.[flag] },
    }));
  };

  const saveGrants = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const grants: { module: string; action: string; granted: boolean }[] = [];
      for (const m of modules) {
        for (const a of ACTIONS) {
          grants.push({ module: m.code, action: a.code, granted: grantMatrix[m.code]?.[a.code] ?? false });
        }
      }
      await apiService.setUserPermissions(selectedId, grants);
      await openUser(selectedId);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleGrant = (module: string, action: string) => {
    setGrantMatrix((prev) => ({
      ...prev,
      [module]: { ...prev[module], [action]: !prev[module]?.[action] },
    }));
  };

  const setModuleAll = (moduleCode: string, value: boolean) => {
    setGrantMatrix((prev) => ({
      ...prev,
      [moduleCode]: { read: value, write: value, critical: value },
    }));
  };

  if (loading) return <div className="p-6 text-gray-500">Cargando usuarios…</div>;

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Usuarios y permisos</h1>
      <p className="text-gray-600 mb-6">
        Solo personal del sistema (sin clientes). Asigná sucursales, cajas y módulos de menú.
      </p>

      {error && <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-gray-700">Personal</span>
            <button
              type="button"
              onClick={startCreate}
              className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              + Agregar usuario
            </button>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Usuario</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Rol</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-4 py-6 text-center text-gray-500 text-sm">
                    No hay usuarios de sistema.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => openUser(u.id)}
                    className={`cursor-pointer hover:bg-blue-50 ${selectedId === u.id && mode === 'edit' ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-4 py-2">{u.firstName} {u.lastName}</td>
                    <td className="px-4 py-2 text-xs font-mono">{u.role}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="lg:col-span-2 bg-white border rounded-lg p-4">
          {mode === 'create' ? (
            <div className="space-y-4 max-w-xl">
              <h2 className="text-lg font-semibold">Nuevo usuario</h2>
              <p className="text-sm text-gray-500">
                Creá el usuario con rol y, si querés, una plantilla de permisos y sucursales. Después podés ajustar cajas y módulos.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-sm block sm:col-span-1">
                  <span className="text-gray-600">Nombre</span>
                  <input
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    value={createForm.firstName}
                    onChange={(e) => setCreateForm((f) => ({ ...f, firstName: e.target.value }))}
                  />
                </label>
                <label className="text-sm block">
                  <span className="text-gray-600">Apellido</span>
                  <input
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    value={createForm.lastName}
                    onChange={(e) => setCreateForm((f) => ({ ...f, lastName: e.target.value }))}
                  />
                </label>
                <label className="text-sm block sm:col-span-2">
                  <span className="text-gray-600">Email</span>
                  <input
                    type="email"
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    value={createForm.email}
                    onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </label>
                <label className="text-sm block">
                  <span className="text-gray-600">Contraseña</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    value={createForm.password}
                    onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                  />
                </label>
                <label className="text-sm block">
                  <span className="text-gray-600">Teléfono (opcional)</span>
                  <input
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    value={createForm.phone}
                    onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                </label>
                <label className="text-sm block">
                  <span className="text-gray-600">Rol</span>
                  <select
                    className="mt-1 w-full border rounded-md px-3 py-2 disabled:bg-gray-50"
                    value={createForm.role}
                    disabled={Boolean(createForm.legacyTemplate)}
                    onChange={(e) => setCreateForm((f) => ({ ...f, role: e.target.value }))}
                  >
                    {STAFF_ROLES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm block">
                  <span className="text-gray-600">Plantilla de permisos (opcional)</span>
                  <select
                    className="mt-1 w-full border rounded-md px-3 py-2"
                    value={createForm.legacyTemplate}
                    onChange={(e) => {
                      const legacyTemplate = e.target.value;
                      const roleFromTemplate: Record<string, string> = {
                        ADMINISTRADOR: 'ADMIN',
                        SUPERVISOR: 'MANAGER',
                        CAJERO: 'COLLECTION_OFFICER',
                        OPERADOR: 'LOAN_OFFICER',
                        CONSULTA: 'AUDITOR',
                      };
                      setCreateForm((f) => ({
                        ...f,
                        legacyTemplate,
                        role: legacyTemplate ? (roleFromTemplate[legacyTemplate] ?? f.role) : f.role,
                      }));
                    }}
                  >
                    <option value="">Sin plantilla (asignar después)</option>
                    {(catalog?.legacyRoles ?? []).map((r: any) => (
                      <option key={r.code} value={r.code}>{r.code}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">Sucursales iniciales (opcional)</p>
                <div className="flex flex-wrap gap-3">
                  {branches.map((b) => (
                    <label key={b.id} className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={createForm.branchIds.includes(b.id)}
                        onChange={() =>
                          setCreateForm((f) => ({
                            ...f,
                            branchIds: f.branchIds.includes(b.id)
                              ? f.branchIds.filter((x) => x !== b.id)
                              : [...f.branchIds, b.id],
                          }))
                        }
                      />
                      {b.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={createUser}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
                >
                  {busy ? 'Creando…' : 'Crear usuario'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => { setMode('idle'); setError(null); }}
                  className="px-4 py-2 border rounded-md text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : !selectedId || !detail ? (
            <p className="text-gray-500 text-sm">Seleccioná un usuario para editar sucursales, cajas y módulos, o agregá uno nuevo.</p>
          ) : (
            <div>
              <h2 className="text-lg font-semibold mb-1">
                {detail.user.firstName} {detail.user.lastName}
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                {detail.user.email} · {detail.user.role}
                {detail.user.isActive === false ? ' · inactivo' : ''}
              </p>

              <div className="flex flex-wrap gap-2 mb-4">
                {(catalog?.legacyRoles ?? []).map((r: any) => (
                  <button
                    key={r.code}
                    type="button"
                    disabled={busy}
                    onClick={() => applyLegacy(r.code)}
                    className="px-3 py-1 text-xs rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {r.code}
                  </button>
                ))}
              </div>

              <div className="flex gap-2 border-b mb-4">
                {(['profile', 'branches', 'cash', 'modules'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    className={`px-3 py-2 text-sm ${tab === t ? 'border-b-2 border-blue-600 text-blue-700 font-medium' : 'text-gray-600'}`}
                  >
                    {t === 'profile'
                      ? 'Datos'
                      : t === 'branches'
                        ? 'Sucursales'
                        : t === 'cash'
                          ? 'Cajas'
                          : 'Módulos'}
                  </button>
                ))}
              </div>

              {tab === 'profile' && (
                <div className="space-y-4 max-w-xl">
                  <p className="text-sm text-gray-500">
                    Editá identidad de acceso. La contraseña solo se cambia si completás el campo.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="text-sm block">
                      <span className="text-gray-600">Nombre</span>
                      <input
                        className="mt-1 w-full border rounded-md px-3 py-2"
                        value={profileForm.firstName}
                        onChange={(e) => setProfileForm((f) => ({ ...f, firstName: e.target.value }))}
                      />
                    </label>
                    <label className="text-sm block">
                      <span className="text-gray-600">Apellido</span>
                      <input
                        className="mt-1 w-full border rounded-md px-3 py-2"
                        value={profileForm.lastName}
                        onChange={(e) => setProfileForm((f) => ({ ...f, lastName: e.target.value }))}
                      />
                    </label>
                    <label className="text-sm block sm:col-span-2">
                      <span className="text-gray-600">Email</span>
                      <input
                        type="email"
                        className="mt-1 w-full border rounded-md px-3 py-2"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm((f) => ({ ...f, email: e.target.value }))}
                      />
                    </label>
                    <label className="text-sm block">
                      <span className="text-gray-600">Teléfono</span>
                      <input
                        className="mt-1 w-full border rounded-md px-3 py-2"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm((f) => ({ ...f, phone: e.target.value }))}
                      />
                    </label>
                    <label className="text-sm block">
                      <span className="text-gray-600">Rol de sistema</span>
                      <select
                        className="mt-1 w-full border rounded-md px-3 py-2"
                        value={profileForm.role}
                        onChange={(e) => setProfileForm((f) => ({ ...f, role: e.target.value }))}
                      >
                        {STAFF_ROLES.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="text-sm block sm:col-span-2">
                      <span className="text-gray-600">Nueva contraseña (opcional)</span>
                      <input
                        type="password"
                        autoComplete="new-password"
                        className="mt-1 w-full border rounded-md px-3 py-2"
                        placeholder="Dejar vacío para no cambiar"
                        value={profileForm.password}
                        onChange={(e) => setProfileForm((f) => ({ ...f, password: e.target.value }))}
                      />
                    </label>
                    <label className="flex items-center gap-2 text-sm sm:col-span-2">
                      <input
                        type="checkbox"
                        checked={profileForm.isActive}
                        onChange={(e) => setProfileForm((f) => ({ ...f, isActive: e.target.checked }))}
                      />
                      Usuario activo (puede iniciar sesión)
                    </label>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={saveProfile}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
                  >
                    {busy ? 'Guardando…' : 'Guardar datos'}
                  </button>
                </div>
              )}

              {tab === 'branches' && (
                <div>
                  <div className="flex flex-wrap gap-3 mb-4">
                    {branches.map((b) => (
                      <label key={b.id} className="flex items-center gap-1 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedBranches.includes(b.id)}
                          onChange={() => setSelectedBranches((prev) =>
                            prev.includes(b.id) ? prev.filter((x) => x !== b.id) : [...prev, b.id],
                          )}
                        />
                        {b.name}
                      </label>
                    ))}
                  </div>
                  <button disabled={busy} onClick={saveBranches} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50">Guardar sucursales</button>
                </div>
              )}

              {tab === 'cash' && (
                <div className="text-sm space-y-3">
                  <p className="text-gray-500">
                    Asigná una o más cajas. El empleado solo podrá abrir las marcadas con «Abrir».
                    Marcá una como <strong>predeterminada</strong>.
                  </p>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {registers.map((r) => {
                      const assigned = Boolean(cashAccessMap[r.id]);
                      const flags = cashAccessMap[r.id];
                      return (
                        <div key={r.id} className="border rounded px-3 py-2">
                          <label className="flex items-center gap-2 font-medium">
                            <input
                              type="checkbox"
                              checked={assigned}
                              onChange={() => toggleCashRegister(r.id)}
                            />
                            {r.branch?.name ?? 'Sucursal'} — Caja {r.number}
                            {r.type && (
                              <span className="text-xs font-normal text-gray-500">({r.type})</span>
                            )}
                          </label>
                          {assigned && flags && (
                            <div className="flex flex-wrap gap-3 mt-2 ml-6 text-xs items-center">
                              {(['canOpen', 'canClose', 'canMove', 'canTransfer', 'canAudit'] as const).map((flag) => (
                                <label key={flag} className="flex items-center gap-1">
                                  <input
                                    type="checkbox"
                                    checked={flags[flag]}
                                    onChange={() => toggleCashFlag(r.id, flag)}
                                  />
                                  {flag === 'canOpen' ? 'Abrir' : flag === 'canClose' ? 'Cerrar' : flag === 'canMove' ? 'Mover' : flag === 'canTransfer' ? 'Transferir' : 'Arqueo'}
                                </label>
                              ))}
                              <label className="flex items-center gap-1 font-medium text-blue-800">
                                <input
                                  type="radio"
                                  name={`default-cash-${selectedId}`}
                                  checked={flags.isDefault}
                                  onChange={() => setDefaultCashRegister(r.id)}
                                />
                                Predeterminada
                              </label>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <button disabled={busy} onClick={saveCashAccess} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50">
                    Guardar cajas
                  </button>
                </div>
              )}

              {tab === 'modules' && (
                <div>
                  <p className="text-sm text-gray-500 mb-3">
                    Activá qué ve cada usuario en el menú (por pantalla). <strong>Ver</strong> = entrar,
                    <strong> Editar</strong> = crear/modificar, <strong>Crítico</strong> = operaciones sensibles.
                    Sin Ver, el ítem no aparece en el menú.
                  </p>
                  <div className="overflow-x-auto border rounded mb-4 max-h-[28rem] overflow-y-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Módulo / funcionalidad</th>
                          {ACTIONS.map((a) => (
                            <th key={a.code} className="px-2 py-2 text-center">{a.label}</th>
                          ))}
                          <th className="px-2 py-2 text-center">Todo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {moduleGroups.map((group) => (
                          <Fragment key={`g-${group.key}`}>
                            <tr className="bg-slate-100 border-t">
                              <td colSpan={5} className="px-3 py-1.5 text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                                {group.label}
                              </td>
                            </tr>
                            {group.items.map((m) => (
                              <tr key={m.code} className="border-t align-top">
                                <td className="px-3 py-2">
                                  <div className="font-medium text-gray-900">{m.label}</div>
                                  <div className="font-mono text-[10px] text-gray-400">{m.code}</div>
                                  {!!m.criticalPoints?.length && (
                                    <p className="mt-1 text-[10px] text-amber-800 leading-snug">
                                      Crítico habilita: {m.criticalPoints.join(', ')}
                                    </p>
                                  )}
                                </td>
                                {ACTIONS.map((a) => (
                                  <td key={a.code} className="px-2 py-2 text-center">
                                    <input
                                      type="checkbox"
                                      checked={grantMatrix[m.code]?.[a.code] ?? false}
                                      onChange={() => toggleGrant(m.code, a.code)}
                                      aria-label={`${m.label} ${a.label}`}
                                    />
                                  </td>
                                ))}
                                <td className="px-2 py-2 text-center">
                                  <button
                                    type="button"
                                    className="text-[10px] text-blue-700 hover:underline"
                                    onClick={() => setModuleAll(m.code, true)}
                                  >
                                    On
                                  </button>
                                  {' / '}
                                  <button
                                    type="button"
                                    className="text-[10px] text-gray-500 hover:underline"
                                    onClick={() => setModuleAll(m.code, false)}
                                  >
                                    Off
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button disabled={busy} onClick={saveGrants} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50">
                    Guardar permisos
                  </button>
                  {detail.hasExplicitGrants && (
                    <p className="text-xs text-amber-700 mt-2">
                      Este usuario tiene grants explícitos (sobreescriben defaults del rol).
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
